export const meta = {
  id: '33-advanced-valence',
  order: 33,
  titleFi: 'Valenssi',
  titleEn: 'Advanced valence',
  duolingoUnit: 'duo-39-advanced-valence',
  // lēda-2 'full': July's Halloween line (hl-2027-07, "full of terrors", together/halloween); with the rising pace
  // (2026-10-14) the quotas would bring it only in lesson 48
  lemmaIds: ['egry', 'anektogon', 'qaedar', 'udēmagon', 'limagon', 'ilimagon', 'lēda-2'],
  exampleIds: [
    'ex-cd132c88ff0f',
    'ex-d8de6484395c',
    'got-s7e02-007',
    'hotd-s2e08-001',
    'ex-bf9b1d5cf983',
    'ex-c7f544442e73',
    'ex-8f07c88f4322',
    'ex-8172e4dd91f9',
    'ex-94d60d0e16c3',
    'ex-cbb533b72fd0',
    'ex-2ea7ff9cde65',
    'ex-7dd8585cfa70',
    'ex-5d5034973f37',
    'ex-35b62de6a290',
    'ex-4a026762a612',
    'ex-af0a31299a55',
    'ex-d94830f39d1e',
    'ex-7791746493c8',
    'ex-32e999ed5dc7',
    'ex-70853508a533',
  ],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Advanced_valence',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Verbal_Prefixes',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Applicatives',
    'https://wiki.languageinvention.com/index.php?title=anektogon',
    'https://wiki.languageinvention.com/index.php?title=udēmagon',
    'https://wiki.languageinvention.com/index.php?title=ilimagon',
    'https://wiki.languageinvention.com/index.php?title=vellaros',
    'https://dothraki.com/2014/02/relative-clauses-in-high-valyrian/#comment-7664',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammar: ['valence'],
  grammarAlso: ['prefixes'],
  showLines: ['got-s7e02-007', 'hotd-s2e08-001'],
  pronunciation: { focus: 'stress', words: ['anektogon', 'udēmagon', 'limagon', 'ilimagon', 'imundagon'], lines: ['got-s7e02-007'] },
  letter: {
    fi: 'Kirjoita kolme lausetta tämän viikon etuliitteellisillä verbeillä.',
    en: 'Write three sentences with this week’s prefixed verbs.',
  },
  scene: 'hotd-s2e08-1',
}

# Valenssi

Tähän asti oppitunnit eivät ole kertoneet aivan koko totuutta. Olet nähnyt monta verbiä, jotka alkavat <Hv>u</Hv>- tai <Hv>v</Hv>-, <Hv>i</Hv>- tai <Hv>j</Hv>-äänteellä, ja joitakin, jotka alkavat <Hv>h</Hv>-, <Hv>a</Hv>-, <Hv>s</Hv>- tai <Hv>z</Hv>-äänteellä, eivätkä ne ole olleet niin yksinkertaisia kuin miltä ne näyttivät. Korkeavalyriassa on **erityinen pääluokkajärjestelmä**: verbin etuliite nostaa jonkin lauseenjäsenen erityisasemaan ja muuttaa lauseen merkitystä hieman.

Järjestelmä on hyvin vanha ja jo väistymässä. Siinä vaiheessa, kun kieli on kehittynyt astaporinvalyrian kaltaiseksi, se on kadonnut kokonaan. Korkeavalyriassa se on kuitenkin vielä enimmäkseen elävä, ja kun ymmärrät sen, osaat käyttää kieltä paremmin. Moni tuttu sana aukeaa nyt:

- <Hv glossed>vurnegon</Hv> ’tarkastella’ ← <Hv glossed>urnegon</Hv> ’nähdä’ (<LessonLink n={29}>oppitunti 29</LessonLink>)
- <Hv glossed>jenigon</Hv> ’koskettaa, vaivata’ ← <Hv glossed>renigon</Hv> ’koskettaa (kädellä)’
- <Hv glossed>izūgagon</Hv> ’pelätä’ ← <Hv>zūgagon</Hv>, <Hv glossed>jōzigon</Hv> ’olla ylpeä’ ← <Hv glossed>hōzigon</Hv> ’paisua’ (<LessonLink n={32}>oppitunti 32</LessonLink>)
- <Hv glossed>spryjagon</Hv> ’tuhota’ ← <Hv>pryjagon</Hv>

Etuliitteitä on kolme, ja ne sulkevat toisensa pois: välinepassiivi, lokatiivinen applikatiivi ja obliikvinen applikatiivi.

<Note>
Suomi ei auta tässä juuri lainkaan. Suomessakin johtimet muuttavat sitä, kuka tekee mitä (*istua* → *istuttaa*), mutta suomi ei koskaan nosta paikkaa tai edunsaajaa verbin objektiksi, eikä se välitä siitä, pystyykö subjekti toimimaan omasta tahdostaan.
</Note>

## Välinepassiivi: kun tekijä ei voi tahtoa

Kun verbillä on subjekti, oletuksena on, että subjektilla on henkinen ja fyysinen kyky tehdä teko, vaikka teko olisikin tahaton (kuten kaatuminen). Jos subjekti ei voisi **koskaan** toimia omasta tahdostaan, esimerkiksi koska se on eloton, verbistä käytetään erityistä muotoa, **välinepassiivia**. Valaiseva esimerkki:

<Interlinear hv="Val|a hav|on nekto|s." gloss="mies-NOM leipä-ACC leikata-PRS.3SG" fi="Mies leikkaa leipää." />

<Interlinear hv="Egr|y hav|on anekto|s." gloss="veitsi-NOM leipä-ACC INS.PASS-leikata-PRS.3SG" fi="Veitsi leikkaa leipää." />

Mies voi *käyttää* veistä leivän leikkaamiseen, mutta veitsi itse ei voi tehdä mitään, ei koskaan. Valyria merkitsee tämän kielioppiin. Suomessa *veitsi leikkaa* on aivan tavallinen lause, eikä verbi muutu.

<Pronounce word="anektogon" />

<Example id="ex-cd132c88ff0f" />

<Example id="ex-d8de6484395c" />

Tämä tarkoittaa, että aiempien oppituntien lauseet eivät aina ole aivan tarkkoja: aina kun subjektina on eloton substantiivi, pitäisi käyttää välinepassiivia. Ajan mittaan valyrian puhujat tosin käyttivät sitä yhä epäjohdonmukaisemmin. Molemmat tavat näkyvät rinnakkain, kun talvi tulee:

<Example id="ex-bf9b1d5cf983" />

<Line id="hotd-s2e08-001" />

<Line id="got-s7e02-007" />

Daemon ja Melisandre sanovat <Hv>amāzis</Hv>, eivät <Hv>māzis</Hv>: talvi ja pitkä yö eivät tule omasta tahdostaan.

<Example id="ex-c7f544442e73" />

<Example id="ex-8f07c88f4322" />

### Muodostaminen

Välinepassiivin etuliite riippuu verbin ensimmäisestä äänteestä:

- **s-** verbeihin, jotka alkavat <Hv>p</Hv>, <Hv>t</Hv>, <Hv>k</Hv> tai <Hv>q</Hv>: <Hv>pryjagon</Hv> → <Hv>spryjagon</Hv>
- **z-** verbeihin, jotka alkavat <Hv>b</Hv>, <Hv>d</Hv>, <Hv>g</Hv>, <Hv>l</Hv> tai <Hv>r</Hv>: <Hv>geltigon</Hv> → <Hv glossed>zgeltigon</Hv> ’peittää’. Huomaa, että yhdistelmästä *zr* tulee *j*: <Hv>renigon</Hv> → <Hv>jenigon</Hv>.
- **h-** verbeihin, jotka alkavat millä tahansa vokaalilla paitsi *e*:llä ja *o*:lla.
- **a-** kaikkiin muihin: <Hv>nektogon</Hv> → <Hv>anektogon</Hv>, <Hv>māzigon</Hv> → <Hv>amāzigon</Hv>, <Hv>ezīmagon</Hv> → <Hv glossed>aezīmagon</Hv> ’halkaista’.

### Tekijä ja väline

Alkuperäisen tekijän voi tuoda takaisin lauseeseen <Hv l="ondoso">ondoso</Hv>-rakenteella, samoin kuin passiivissa (<LessonLink n={30}>oppitunti 30</LessonLink>). Jos välinepassiivi vielä pannaan tavalliseen passiiviin, entinen subjekti eli väline palaa **instrumentaaliin**:

<Example id="ex-8172e4dd91f9" />

<Example id="ex-94d60d0e16c3" />

<Example id="ex-cbb533b72fd0" />

<Paradigm lemma="egry" numbers="sg" />

## Lokatiivinen applikatiivi: istua valaan päällä

**Lokatiivinen applikatiivi** ottaa paikan ilmauksen ja tekee siitä verbin suoran objektin. Tämä objekti on **datiivissa** – paitsi jos se on yksikössä ja seisoo heti verbin edellä ja verbi alkaa konsonantilla. Silloin se on **genetiivissä**.

<Interlinear hv="Qaed|ār dēma|tan." gloss="valas-LOC istua-PRF.1SG" fi="Istuin valaan päällä." />

<Interlinear hv="Qaedr|ot udēma|tan." gloss="valas-DAT APPL.LOC-istua-PRF.1SG" fi="Istuin valaan päällä." />

Molemmat lauseet tarkoittavat ’istuin valaan päällä’, mutta merkityksessä on pieni ero. Applikatiivissa objektin oletetaan olevan suoraan teon kohteena: istuminen kohdistuu valaaseen eikä vain tapahdu sen päällä.

<Compare fi="valaa|n päällä" hv="qaed|ār" label="genetiivi + postpositio ~ lokatiivi" />

<Pronounce word="qaedar" />

<Example id="ex-70853508a533" />

<Hv>Udēmagon</Hv> alkaa vokaalilla, joten sen objekti on datiivissa. <LessonLink n={29}>Oppitunnin 29</LessonLink> <Hv>vurnegon</Hv> taas alkaa konsonantilla, joten yksikössä heti verbin edellä oleva objekti voi olla genetiivissä:

<Example id="ex-2ea7ff9cde65" />

<Example id="ex-7dd8585cfa70" />

<Example id="ex-5d5034973f37" />

<Note>
Lähteet eroavat hieman: Petersonin (2014) mukaan konsonantilla alkavan applikatiiviverbin objekti on genetiivissä ja vokaalilla alkavan datiivissa; wikin kielioppisivu sallii genetiivin vain yksikössä. Ohjeiden sääntö on näistä varovaisin.
</Note>

Lokatiiviseen applikatiiviin voi lisätä paikkaa ilmaisevan postposition etuliitteeksi, jolloin merkitys tarkentuu. Postpositio tulee heti <Hv>u</Hv>- tai <Hv>v</Hv>-etuliitteen eteen: <Hv glossed>bē</Hv> ’päällä’ + <Hv>u-</Hv> + <Hv glossed>pyghagon</Hv> ’hypätä’ → <Hv glossed>bēupyghagon</Hv> ’hypätä jonkin päälle’.

### Muodostaminen

- Verbin alun <Hv>h</Hv> vaihtuu <Hv>v</Hv>:ksi.
- **v-** verbeihin, jotka alkavat millä tahansa vokaalilla paitsi *e*:llä tai *ē*:llä: <Hv>urnegon</Hv> → <Hv>vurnegon</Hv>.
- **u-** kaikkiin muihin: <Hv>dēmagon</Hv> → <Hv>udēmagon</Hv>, <Hv>sōvegon</Hv> → <Hv>usōvegon</Hv>.

Muistatko <LessonLink n={31}>oppitunnin 31</LessonLink> housut? <Hv>Vellaros</Hv> on johdettu juuri tällä etuliitteellä. Säännön mukaan odottaisi muotoa <Hv>uellaros</Hv>, koska kantasana alkaa *e*:llä. Sanakirja toteaa, että <Hv>vellaros</Hv> on odotuksenvastainen muoto, mutta molemmat käyvät.

## Obliikvinen applikatiivi: itkeä jonkun puolesta

**Obliikvinen applikatiivi** ottaa muun kuin paikkaa ilmaisevan määreen ja tekee siitä suoran objektin. Objekti on **akkusatiivissa**.

<Interlinear hv="Zijo syt lima|tan." gloss="hän-GEN hyväksi itkeä-PRF.1SG" fi="Itkin hänen puolestaan." />

<Interlinear hv="Ziry ilima|tan." gloss="hän-ACC APPL.OBL-itkeä-PRF.1SG" fi="Itkin häntä." />

Edellisen oppitunnin <Hv>syt</Hv>-rakenne siis tiivistyy verbin etuliitteeksi, ja <Hv glossed>zijo</Hv> ’hänen’ muuttuu objektiksi <Hv glossed>ziry</Hv> ’hänet’. Suomen *itkeä jotakuta* on sattumalta lähes sama rakenne.

<Pronounce word="ilimagon" />

<Example id="ex-4a026762a612" />

<Example id="ex-af0a31299a55" />

Obliikvisen applikatiivin merkitykset ovat usein idiomaattisia, joten ne kannattaa opetella kokonaisina sanoina: <Hv glossed>hōzigon</Hv> ’paisua’ → <Hv glossed>jōzigon</Hv> ’olla ylpeä jostakin’, <Hv glossed>iōragon</Hv> ’seistä’ → <Hv glossed>jiōragon</Hv> ’ottaa vastaan’, <Hv glossed>derēbagon</Hv> ’kerätä’ → <Hv glossed>iderēbagon</Hv> ’valita jollekulle’.

Myös tähän voi lisätä postposition etuliitteeksi, tällä kertaa muun kuin paikkaa ilmaisevan. Se tulee heti <Hv>i</Hv>- tai <Hv>j</Hv>-etuliitteen eteen: <Hv>syt</Hv> + <Hv>iderēbagon</Hv> → <Hv>sytiderēbagon</Hv>.

### Muodostaminen

- Verbin alun <Hv>h</Hv> vaihtuu <Hv>j</Hv>:ksi: <Hv>hōzigon</Hv> → <Hv>jōzigon</Hv>.
- **j-** verbeihin, jotka alkavat <Hv>i</Hv>, <Hv>ī</Hv>, <Hv>u</Hv>, <Hv>ū</Hv>, <Hv>y</Hv>, <Hv>ȳ</Hv> tai diftongilla: <Hv>iōragon</Hv> → <Hv>jiōragon</Hv>.
- **i-** kaikkiin muihin: <Hv>limagon</Hv> → <Hv>ilimagon</Hv>, <Hv>mundagon</Hv> → <Hv>imundagon</Hv>.

## Applikatiivi passiivissa

Kun applikatiivi pannaan passiiviin, sen **uusi objekti** muuttuu subjektiksi. Siksi <Hv glossed>Ziry ilimataks</Hv> tarkoittaa ’hänen puolestaan itkettiin’. Itkijä ei ole hän vaan joku muu; hän on se, jonka vuoksi itketään.

<Example id="ex-d94830f39d1e" />

<Example id="ex-35b62de6a290" />

<Note>
Wikissä sama lause on kahdesti eri sijoissa: <Hv>Aerys iderēbaks</Hv> (nominatiivi) ja <Hv glossed>Aeri iderēbaks</Hv> (akkusatiivi), molemmat ’Aerysille valitaan’. Tips & Notesin säännön mukaan passiivin subjekti on nominatiivissa, joten <Hv>Aerys</Hv> on odotettu muoto. Pronominissa <Hv>ziry</Hv> ero ei näy, koska nominatiivi ja akkusatiivi ovat samat.

<Example id="ex-7791746493c8" />

<Example id="ex-32e999ed5dc7" />
</Note>

## Sanajärjestys

Ennen vanhaan nostettu lauseenjäsen sijoitettiin aina suoraan verbin eteen. Daenerysin aikaan sääntö oli jo höltynyt, mutta paikka verbin edellä on yhä applikatiivien ja välinepassiivin **oletusjärjestys**. Suomen vapaaseen sanajärjestykseen tottuneelle tämä on muistamisen arvoinen sääntö: kuten <LessonLink n={31}>oppitunnin 31</LessonLink> määreissä, sanan paikka kantaa merkitystä.

Huomaa kuitenkin, että Tips & Notesin oma välinepassiiviesimerkki <Hv fi="Veitsi leikkaa leipää">Egry havon anektos</Hv> sijoittaa objektin subjektin ja verbin väliin, ja niin tekee moni muukin lause. Pidä oletusjärjestys mielessä, mutta älä hämmenny, jos esimerkkilauseissa näkyy toisin.

## Harjoituslauseita

<Practice hv="Egry melvo anektos." fi="Veitsi viipaloi päärynää." />

<Practice hv="Qurdot udēmatan." fi="Istuin pöydällä." />

<Practice hv="Dāri ilimatan." fi="Itkin kuninkaan puolesta." />

<Practice hv="Dārys ilimataks." fi="Kuninkaan puolesta itkettiin." />

## Muistettavaksi

- **Välinepassiivi** (s-, z-, h-, a-): eloton subjekti, joka ei voi tahtoa. <Hv fi="Veitsi leikkaa leipää.">Egry havon anektos.</Hv>
- **Lokatiivinen applikatiivi** (v-, u-): paikasta tulee objekti, datiivissa (tai genetiivissä, jos se on yksikössä heti konsonanttialkuisen verbin edellä).
- **Obliikvinen applikatiivi** (j-, i-): edunsaajasta tai muusta määreestä tulee objekti, akkusatiivissa. Merkitykset ovat usein idiomaattisia.
- Passiivissa applikatiivin objektista tulee subjekti.
- Nostettu lauseenjäsen seisoo oletuksena suoraan verbin edellä.

<Vocab />

<Check />
